import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Package, 
  FolderTree, 
  Settings, 
  Mail,
  HelpCircle,
  Plus,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import ProductsManager from './ProductsManager';
import CategoriesManager from './CategoriesManager';
import EmailsManager from './EmailsManager';
import SettingsManager from './SettingsManager';
import FAQManager from './FAQManager';
import { productsAPI, categoriesAPI, emailsAPI } from '../../services/api';

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/products', icon: Package, label: 'Products' },
    { path: '/admin/categories', icon: FolderTree, label: 'Categories' },
    { path: '/admin/emails', icon: Mail, label: 'Email List' },
    { path: '/admin/faq', icon: HelpCircle, label: 'FAQ' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return currentPath === item.path;
    }
    return currentPath.startsWith(item.path);
  };

  // Get stats from API
  const [stats, setStats] = useState({ products: 0, categories: 0, emails: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [productsRes, categoriesRes, emailsRes] = await Promise.all([
          productsAPI.getAll(),
          categoriesAPI.getAll(),
          emailsAPI.getAll()
        ]);
        setStats({
          products: productsRes.data.length,
          categories: categoriesRes.data.length,
          emails: emailsRes.data.length
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [currentPath]);

  return (
    <div className="min-h-screen bg-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-4 sticky top-24">
              <h2 className="text-lg font-bold text-white mb-4 px-2">Admin Panel</h2>
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isActive(item)
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            <Routes>
              <Route
                index
                element={
                  <div className="space-y-6">
                    <div>
                      <h1 className="text-3xl font-bold text-white">Dashboard</h1>
                      <p className="text-zinc-400 mt-1">Welcome to the admin panel</p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                        <Package className="h-8 w-8 text-amber-500 mb-3" />
                        <p className="text-3xl font-bold text-white">
                          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.products}
                        </p>
                        <p className="text-zinc-400">Products</p>
                      </div>
                      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                        <FolderTree className="h-8 w-8 text-amber-500 mb-3" />
                        <p className="text-3xl font-bold text-white">
                          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.categories}
                        </p>
                        <p className="text-zinc-400">Categories</p>
                      </div>
                      <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                        <Mail className="h-8 w-8 text-amber-500 mb-3" />
                        <p className="text-3xl font-bold text-white">
                          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.emails}
                        </p>
                        <p className="text-zinc-400">Emails Collected</p>
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                      <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={() => navigate('/admin/products/new')}
                          className="bg-amber-500 hover:bg-amber-600 text-black"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Product
                        </Button>
                        <Button
                          onClick={() => navigate('/admin/categories')}
                          variant="outline"
                          className="border-zinc-700 text-white hover:bg-zinc-800"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Category
                        </Button>
                      </div>
                    </div>

                    {/* Recent activity placeholder */}
                    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
                      <h3 className="text-lg font-semibold text-white mb-4">Getting Started</h3>
                      <div className="space-y-3">
                        <Link
                          to="/admin/categories"
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                        >
                          <span className="text-zinc-300">1. Set up categories</span>
                          <ChevronRight className="h-5 w-5 text-zinc-500" />
                        </Link>
                        <Link
                          to="/admin/products/new"
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                        >
                          <span className="text-zinc-300">2. Add products</span>
                          <ChevronRight className="h-5 w-5 text-zinc-500" />
                        </Link>
                        <Link
                          to="/admin/settings"
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                        >
                          <span className="text-zinc-300">3. Configure store settings</span>
                          <ChevronRight className="h-5 w-5 text-zinc-500" />
                        </Link>
                      </div>
                    </div>
                  </div>
                }
              />
              <Route path="products/*" element={<ProductsManager />} />
              <Route path="categories" element={<CategoriesManager />} />
              <Route path="emails" element={<EmailsManager />} />
              <Route path="faq" element={<FAQManager />} />
              <Route path="settings" element={<SettingsManager />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
